import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  getDocuments, 
  deleteDocument,
  forceDeleteDocument,
  cancelDocument,
  duplicateDocument,
  getDocumentPdf, 
  validateDocument, 
  convertQuote,
  getPayments,
  createPayment,
  deletePayment,
  cancelAllPayments,
  createCreditNoteFromInvoice,
  rectifyInvoice,
  getInvoiceLinesForCredit,
  changeQuoteStatus,
  getEInvoiceStatus,
  prepareEInvoice,
  transmitEInvoice,
  getEInvoiceXml,
  getEInvoiceHistory,
  createDepositInvoice,
  createBalanceInvoice,
  getQuoteDeposits
} from "../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { 
  Plus, 
  Search, 
  Download, 
  Trash2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileX,
  Edit,
  Copy,
  CreditCard,
  FileMinus,
  Eye,
  MoreVertical,
  Undo2,
  FileEdit,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  Zap,
  FileCode,
  History,
  Loader2,
  Receipt,
  Percent
} from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
  quote: "Devis",
  invoice: "Facture",
  credit: "Avoir"
};

const DOC_TYPE_PLURALS = {
  quote: "Devis",
  invoice: "Factures",
  credit: "Avoirs"
};

// Status configuration with custom colors from Studio Raneem palette
// Invoice statuses: draft, pending, paid, partial, overdue, cancelled, rectified
// Quote statuses: draft, pending, accepted, refused, expired
const STATUS_CONFIG = {
  // Common statuses
  draft: { 
    label: "Brouillon", 
    color: "#9CA3AF",
    className: "border" 
  },
  pending: { 
    label: "En attente", 
    color: "#26788A",
    className: "border" 
  },
  // Invoice specific
  paid: { 
    label: "Payée", 
    color: "#16A34A",
    className: "border" 
  },
  partial: { 
    label: "Partiellement payée", 
    color: "#F59E0B",
    className: "border" 
  },
  overdue: { 
    label: "En retard", 
    color: "#DC2626",
    className: "border" 
  },
  cancelled: { 
    label: "Annulée", 
    color: "#6B7280",
    className: "border" 
  },
  rectified: { 
    label: "Rectifiée", 
    color: "#6B7280",
    className: "border" 
  },
  // Quote specific
  accepted: { 
    label: "Accepté", 
    color: "#16A34A",
    className: "border" 
  },
  refused: { 
    label: "Refusé", 
    color: "#DC2626",
    className: "border" 
  },
  expired: { 
    label: "Expiré", 
    color: "#D1D5DB",
    className: "border" 
  },
  // Legacy - map to pending
  validated: { 
    label: "En attente", 
    color: "#26788A",
    className: "border" 
  },
  sent: { 
    label: "En attente", 
    color: "#26788A",
    className: "border" 
  }
};

// Filter tabs by document type
const INVOICE_STATUS_TABS = [
  { value: "all", label: "Tous" },
  { value: "draft", label: "Brouillons" },
  { value: "pending", label: "En attente" },
  { value: "partial", label: "Partielles" },
  { value: "overdue", label: "En retard" },
  { value: "paid", label: "Payées" }
];

const QUOTE_STATUS_TABS = [
  { value: "all", label: "Tous" },
  { value: "draft", label: "Brouillons" },
  { value: "pending", label: "En attente" },
  { value: "accepted", label: "Acceptés" },
  { value: "refused", label: "Refusés" },
  { value: "expired", label: "Expirés" }
];

const CREDIT_STATUS_TABS = [
  { value: "all", label: "Tous" },
  { value: "draft", label: "Brouillons" },
  { value: "pending", label: "Validés" }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

const formatMonth = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

const DocumentsPage = () => {
  const { type = "invoice" } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "transfer",
    reference: ""
  });
  
  // Credit note dialog
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [creditNoteLines, setCreditNoteLines] = useState([]);
  const [creditNoteType, setCreditNoteType] = useState("total");
  const [creditNoteReason, setCreditNoteReason] = useState("");
  const [creditNoteLoading, setCreditNoteLoading] = useState(false);
  
  // Rectification confirmation
  const [rectifyDialogOpen, setRectifyDialogOpen] = useState(false);
  const [rectifyLoading, setRectifyLoading] = useState(false);

  // E-invoicing
  const [einvoiceStatus, setEinvoiceStatus] = useState(null);
  const [einvoiceHistory, setEinvoiceHistory] = useState([]);
  const [einvoiceLoading, setEinvoiceLoading] = useState(false);
  const [showEinvoiceHistory, setShowEinvoiceHistory] = useState(false);

  // Deposit (acompte)
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositMode, setDepositMode] = useState("percentage"); // "percentage" or "amount"
  const [depositValue, setDepositValue] = useState(30);
  const [depositLoading, setDepositLoading] = useState(false);
  const [quoteDeposits, setQuoteDeposits] = useState(null);

  useEffect(() => {
    loadDocuments();
    setSelectedDoc(null);
  }, [type, filterStatus]);

  useEffect(() => {
    if (selectedDoc && type === "invoice") {
      loadPayments(selectedDoc.id);
      if (selectedDoc.status !== "draft") {
        loadEinvoiceStatus(selectedDoc.id);
      } else {
        setEinvoiceStatus(null);
      }
    }
    if (selectedDoc && type === "quote" && selectedDoc.status !== "draft") {
      loadQuoteDeposits(selectedDoc.id);
    } else {
      setQuoteDeposits(null);
    }
  }, [selectedDoc]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await getDocuments({ 
        doc_type: type,
        status: filterStatus !== "all" ? filterStatus : undefined 
      });
      setDocuments(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async (docId) => {
    try {
      const res = await getPayments({ document_id: docId });
      setPayments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadEinvoiceStatus = async (docId) => {
    try {
      const res = await getEInvoiceStatus(docId);
      setEinvoiceStatus(res.data);
    } catch (err) {
      setEinvoiceStatus(null);
    }
  };

  const handlePrepareEinvoice = async () => {
    if (!selectedDoc) return;
    setEinvoiceLoading(true);
    try {
      await prepareEInvoice(selectedDoc.id);
      toast.success("Document préparé pour e-facturation");
      await loadEinvoiceStatus(selectedDoc.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la préparation");
    } finally {
      setEinvoiceLoading(false);
    }
  };

  const handleTransmitEinvoice = async () => {
    if (!selectedDoc) return;
    setEinvoiceLoading(true);
    try {
      const res = await transmitEInvoice(selectedDoc.id);
      toast.success(res.data.message || "Facture transmise");
      await loadEinvoiceStatus(selectedDoc.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la transmission");
    } finally {
      setEinvoiceLoading(false);
    }
  };

  const handleDownloadXml = async () => {
    if (!selectedDoc) return;
    try {
      const res = await getEInvoiceXml(selectedDoc.id);
      const blob = new Blob([res.data], { type: "application/xml" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `facturx_${selectedDoc.number || selectedDoc.id}.xml`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur lors du téléchargement XML");
    }
  };

  const handleLoadEinvoiceHistory = async () => {
    if (!selectedDoc) return;
    try {
      const res = await getEInvoiceHistory(selectedDoc.id);
      setEinvoiceHistory(res.data);
      setShowEinvoiceHistory(true);
    } catch (err) {
      toast.error("Erreur lors du chargement de l'historique");
    }
  };

  // Deposit (acompte) handlers
  const loadQuoteDeposits = async (quoteId) => {
    try {
      const res = await getQuoteDeposits(quoteId);
      setQuoteDeposits(res.data);
    } catch {
      setQuoteDeposits(null);
    }
  };

  const handleCreateDeposit = async () => {
    if (!selectedDoc) return;
    setDepositLoading(true);
    try {
      const payload = depositMode === "percentage"
        ? { percentage: parseFloat(depositValue) }
        : { amount: parseFloat(depositValue) };
      const res = await createDepositInvoice(selectedDoc.id, payload);
      const inv = res.data;
      toast.success(`Facture d'acompte créée : ${inv.number || 'brouillon'} (${formatCurrency(inv.total_ttc)})`);
      setDepositDialogOpen(false);
      loadQuoteDeposits(selectedDoc.id);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setDepositLoading(false);
    }
  };

  const handleCreateBalance = async () => {
    if (!selectedDoc) return;
    if (!window.confirm("Créer la facture de solde ? Elle inclura toutes les lignes du devis avec déduction des acomptes.")) return;
    try {
      const res = await createBalanceInvoice(selectedDoc.id);
      const inv = res.data;
      toast.success(`Facture de solde créée (${formatCurrency(inv.total_ttc)})`);
      loadQuoteDeposits(selectedDoc.id);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Group documents by month
  const groupedDocs = documents.reduce((groups, doc) => {
    const monthKey = doc.issue_date ? formatMonth(doc.issue_date) : "Sans date";
    if (!groups[monthKey]) {
      groups[monthKey] = { docs: [], total: 0 };
    }
    groups[monthKey].docs.push(doc);
    groups[monthKey].total += doc.total_ttc || 0;
    return groups;
  }, {});

  // Filter by search
  const filteredGroups = Object.entries(groupedDocs).reduce((acc, [month, data]) => {
    const filteredDocs = data.docs.filter(doc => {
      if (!search) return true;
      const q = search.toLowerCase();
      const clientName = doc.customer_snapshot?.company_name || 
        `${doc.customer_snapshot?.first_name || ""} ${doc.customer_snapshot?.last_name || ""}`.trim();
      const snap = doc.customer_snapshot || {};
      // Search in document fields
      if (doc.number?.toLowerCase().includes(q)) return true;
      if (clientName.toLowerCase().includes(q)) return true;
      if (doc.subject?.toLowerCase().includes(q)) return true;
      if (doc.specific_note?.toLowerCase().includes(q)) return true;
      if (doc.details?.toLowerCase().includes(q)) return true;
      if (doc.project_reference?.toLowerCase().includes(q)) return true;
      // Search in client snapshot
      if (snap.email?.toLowerCase().includes(q)) return true;
      if (snap.siret?.toLowerCase().includes(q)) return true;
      if (snap.vat_number?.toLowerCase().includes(q)) return true;
      if (snap.city?.toLowerCase().includes(q)) return true;
      if (snap.address?.toLowerCase().includes(q)) return true;
      if (snap.phone?.includes(q)) return true;
      // Search in line items
      if (doc.lines?.some(line => 
        line.name?.toLowerCase().includes(q) || 
        line.description?.toLowerCase().includes(q)
      )) return true;
      // Search by amount
      if (doc.total_ttc?.toString().includes(q)) return true;
      return false;
    });
    if (filteredDocs.length > 0) {
      acc[month] = { docs: filteredDocs, total: filteredDocs.reduce((sum, d) => sum + (d.total_ttc || 0), 0) };
    }
    return acc;
  }, {});

  const handleDownloadPdf = async (doc) => {
    try {
      const res = await getDocumentPdf(doc.id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${DOC_TYPE_LABELS[doc.doc_type]}_${doc.number || "Brouillon"}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleValidate = async (doc) => {
    if (!window.confirm("Valider ce document ? Cette action est irréversible.")) return;
    try {
      const res = await validateDocument(doc.id);
      loadDocuments();
      setSelectedDoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleConvert = async (doc) => {
    try {
      const res = await convertQuote(doc.id);
      toast.success("Facture créée à partir du devis");
      navigate(`/document/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (doc) => {
    const isDraft = doc.status === "draft";
    const confirmMsg = isDraft
      ? "Supprimer ce brouillon ?"
      : `Supprimer définitivement ${doc.number || "ce document"} ? Cette action est irréversible et supprimera aussi les paiements associés.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    // Double confirmation for validated documents
    if (!isDraft) {
      if (!window.confirm("ATTENTION : Confirmez-vous la suppression définitive ? Le document ne pourra pas être récupéré.")) return;
    }
    
    try {
      if (isDraft) {
        await deleteDocument(doc.id);
      } else {
        await forceDeleteDocument(doc.id);
      }
      toast.success("Document supprimé");
      setSelectedDoc(null);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCancel = async (doc) => {
    if (!window.confirm(`Annuler ${doc.number || "ce document"} ? Le document sera marqué comme annulé.`)) return;
    try {
      await cancelDocument(doc.id);
      toast.success("Document annulé");
      loadDocuments();
      // Refresh selected doc
      const res = await getDocuments({ doc_type: type });
      const updated = res.data.find(d => d.id === doc.id);
      if (updated) setSelectedDoc(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDuplicate = async (doc) => {
    try {
      const res = await duplicateDocument(doc.id);
      toast.success("Copie créée en brouillon");
      navigate(`/document/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la copie");
    }
  };

  const handleAddPayment = async () => {
    if (!selectedDoc) return;
    try {
      await createPayment({
        document_id: selectedDoc.id,
        ...paymentForm
      });
      toast.success("Paiement enregistré");
      setPaymentDialogOpen(false);
      loadDocuments();
      loadPayments(selectedDoc.id);
      // Refresh selected doc
      const updatedDocs = await getDocuments({ doc_type: type });
      const updated = updatedDocs.data.find(d => d.id === selectedDoc.id);
      if (updated) setSelectedDoc(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Cancel a specific payment
  const handleCancelPayment = async (paymentId) => {
    if (!window.confirm("Annuler ce paiement ?")) return;
    try {
      await deletePayment(paymentId);
      toast.success("Paiement annulé");
      loadDocuments();
      loadPayments(selectedDoc.id);
      // Refresh selected doc
      const updatedDocs = await getDocuments({ doc_type: type });
      const updated = updatedDocs.data.find(d => d.id === selectedDoc.id);
      if (updated) setSelectedDoc(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Cancel all payments
  const handleCancelAllPayments = async () => {
    if (!selectedDoc) return;
    const msg = payments.length > 1
      ? `Annuler tous les paiements (${payments.length}) ? La facture repassera en attente de paiement.`
      : "Annuler ce règlement ? La facture repassera en attente de paiement.";
    if (!window.confirm(msg)) return;
    try {
      await cancelAllPayments(selectedDoc.id);
      toast.success("Tous les paiements ont été annulés");
      loadDocuments();
      setPayments([]);
      // Refresh selected doc
      const updatedDocs = await getDocuments({ doc_type: type });
      const updated = updatedDocs.data.find(d => d.id === selectedDoc.id);
      if (updated) setSelectedDoc(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Open credit note dialog
  const handleOpenCreditNoteDialog = async () => {
    if (!selectedDoc) return;
    setCreditNoteLoading(true);
    try {
      const res = await getInvoiceLinesForCredit(selectedDoc.id);
      setCreditNoteLines(res.data.lines.map(l => ({ ...l, include: true })));
      setCreditNoteType("total");
      setCreditNoteReason("");
      setCreditNoteDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du chargement des lignes");
    } finally {
      setCreditNoteLoading(false);
    }
  };

  // Create credit note
  const handleCreateCreditNote = async () => {
    if (!selectedDoc) return;
    setCreditNoteLoading(true);
    try {
      const payload = {
        credit_type: creditNoteType,
        reason: creditNoteReason || undefined
      };
      
      if (creditNoteType === "partial") {
        payload.lines = creditNoteLines
          .filter(l => l.quantity > 0)
          .map(l => ({
            name: l.name,
            description: l.description,
            unit: l.unit,
            quantity: l.quantity,
            unit_price_ht: l.unit_price_ht,
            discount_percent: l.discount_percent || 0,
            discount_amount: l.discount_amount || 0,
            vat_rate: l.vat_rate,
            catalog_item_id: l.catalog_item_id
          }));
      }
      
      const res = await createCreditNoteFromInvoice(selectedDoc.id, payload);
      toast.success("Avoir créé avec succès");
      setCreditNoteDialogOpen(false);
      navigate(`/document/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création de l'avoir");
    } finally {
      setCreditNoteLoading(false);
    }
  };

  // Rectify invoice (one-click)
  const handleRectifyInvoice = async () => {
    if (!selectedDoc) return;
    setRectifyLoading(true);
    try {
      const res = await rectifyInvoice(selectedDoc.id);
      toast.success(`Facture ${selectedDoc.number} rectifiée`);
      setRectifyDialogOpen(false);
      // Navigate to the new draft invoice
      navigate(`/document/${res.data.new_invoice.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la rectification");
    } finally {
      setRectifyLoading(false);
    }
  };

  // Change quote status (Accept/Refuse)
  const handleChangeQuoteStatus = async (newStatus) => {
    if (!selectedDoc) return;
    const confirmMsg = newStatus === "accepted" 
      ? "Marquer ce devis comme accepté ?" 
      : "Marquer ce devis comme refusé ?";
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const res = await changeQuoteStatus(selectedDoc.id, newStatus);
      toast.success(newStatus === "accepted" ? "Devis accepté" : "Devis refusé");
      loadDocuments();
      setSelectedDoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de statut");
    }
  };

  // Update credit note line quantity
  const updateCreditNoteLine = (index, field, value) => {
    setCreditNoteLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid": 
      case "accepted": return <CheckCircle2 size={14} />;
      case "overdue": 
      case "refused": return <AlertTriangle size={14} />;
      case "cancelled": 
      case "rectified": return <FileX size={14} />;
      case "partial": return <Clock size={14} />;
      case "pending": return <Clock size={14} />;
      case "expired": return <Clock size={14} />;
      default: return null;
    }
  };

  // Get status tabs based on document type
  const getStatusTabs = () => {
    switch (type) {
      case "quote": return QUOTE_STATUS_TABS;
      case "credit": return CREDIT_STATUS_TABS;
      default: return INVOICE_STATUS_TABS;
    }
  };

  // Render a status badge with the correct color
  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    return (
      <Badge 
        className="text-xs px-2 py-0.5 border"
        style={{ 
          backgroundColor: `${config.color}15`, 
          color: config.color,
          borderColor: `${config.color}40`
        }}
      >
        {getStatusIcon(status)}
        <span className="ml-1">{config.label}</span>
      </Badge>
    );
  };

  const clientName = selectedDoc?.customer_snapshot?.company_name || 
    `${selectedDoc?.customer_snapshot?.first_name || ""} ${selectedDoc?.customer_snapshot?.last_name || ""}`.trim() ||
    "Client";

  // State for mobile detail view
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // Handle document selection
  const handleSelectDoc = (doc) => {
    setSelectedDoc(doc);
    // On mobile, show detail view
    if (window.innerWidth < 1024) {
      setShowMobileDetail(true);
    }
  };

  // Handle back from mobile detail
  const handleBackToList = () => {
    setShowMobileDetail(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-4 flex items-center justify-between">
        <h1 className="font-heading text-lg lg:text-xl text-slate-900">
          {DOC_TYPE_PLURALS[type]}
        </h1>
        <Button
          data-testid="new-document-btn"
          className="btn-primary text-sm lg:text-base"
          onClick={() => navigate(`/document/new/${type}`)}
        >
          <Plus size={16} className="mr-1 lg:mr-2" />
          <span className="hidden sm:inline">{type === "quote" ? "Nouveau devis" : 
           type === "invoice" ? "Nouvelle facture" : "Nouvel avoir"}</span>
          <span className="sm:hidden">Créer</span>
        </Button>
      </div>

      {/* Content - Master Detail */}
      <div className="flex-1 flex overflow-hidden" data-testid="documents-page">
        {/* Left - List (hidden on mobile when detail is shown) */}
        <div className={`
          w-full lg:w-[55%] border-r border-slate-200 flex flex-col bg-white
          ${showMobileDetail ? 'hidden lg:flex' : 'flex'}
        `}>
          {/* Filters */}
          <div className="p-3 lg:p-4 border-b border-slate-200 space-y-3">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  data-testid="search-documents-input"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 input-field"
                />
              </div>
            </div>
            {/* Tab filters by status */}
            <div className="flex gap-1 flex-wrap">
              {getStatusTabs().map(tab => {
                const isActive = filterStatus === tab.value;
                const tabColor = tab.value === "all" ? "#26788A" : (STATUS_CONFIG[tab.value]?.color || "#26788A");
                return (
                  <button
                    key={tab.value}
                    data-testid={`filter-tab-${tab.value}`}
                    onClick={() => setFilterStatus(tab.value)}
                    className="px-3 py-1.5 text-sm rounded-md transition-all duration-200"
                    style={{
                      backgroundColor: isActive ? tabColor : "transparent",
                      color: isActive ? "white" : "#64748b",
                      border: isActive ? "none" : "1px solid transparent"
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Document List */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Chargement...</div>
            ) : Object.keys(filteredGroups).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400">Aucun document trouvé</p>
                <Button
                  className="mt-4 btn-primary"
                  onClick={() => navigate(`/document/new/${type}`)}
                >
                  <Plus size={16} className="mr-2" />
                  Créer
                </Button>
              </div>
            ) : (
              Object.entries(filteredGroups).map(([month, data]) => (
                <div key={month}>
                  {/* Period Header */}
                  <div className="period-header">
                    <span className="capitalize">{month}</span>
                    <span className="font-mono text-slate-500 text-sm">{formatCurrency(data.total)}</span>
                  </div>
                  {/* Documents */}
                  {data.docs.map((doc) => {
                    const docClientName = doc.customer_snapshot?.company_name || 
                      `${doc.customer_snapshot?.first_name || ""} ${doc.customer_snapshot?.last_name || ""}`.trim();
                    const isSelected = selectedDoc?.id === doc.id;
                    
                    return (
                      <div
                        key={doc.id}
                        data-testid={`document-row-${doc.id}`}
                        className={`list-item ${isSelected ? 'list-item-selected lg:list-item-selected' : ''}`}
                        onClick={() => handleSelectDoc(doc)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
                            <span className="font-mono text-sm font-medium text-slate-900">
                              {doc.number || "Brouillon"}
                            </span>
                            {renderStatusBadge(doc.status)}
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5 truncate">
                            {docClientName || "—"}
                            <span className="hidden sm:inline">
                              {doc.subject && <span className="text-slate-400"> · {doc.subject}</span>}
                            </span>
                          </p>
                        </div>
                        <div className="text-right ml-2 lg:ml-4 flex-shrink-0">
                          <p className="font-mono text-sm font-medium text-slate-900">
                            {formatCurrency(doc.total_ttc)}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {formatDate(doc.issue_date)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right - Detail Panel (full screen on mobile when shown) */}
        <div className={`
          ${showMobileDetail ? 'fixed inset-0 z-50 lg:static lg:z-auto' : 'hidden lg:block'}
          w-full lg:w-[45%] bg-slate-50 overflow-auto
        `}>
          {!selectedDoc ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              <div className="text-center">
                <FileX size={48} className="mx-auto mb-3 text-slate-300" />
                <p>Sélectionnez un document</p>
              </div>
            </div>
          ) : (
            <div className="animate-slideInRight">
              {/* Detail Header */}
              <div className="bg-white border-b border-slate-200 p-4 lg:p-6">
                {/* Mobile back button */}
                <button
                  onClick={handleBackToList}
                  className="lg:hidden flex items-center gap-2 text-slate-600 mb-4 hover:text-slate-900"
                >
                  <ArrowLeft size={20} />
                  <span>Retour à la liste</span>
                </button>
                
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 lg:gap-3 flex-wrap">
                      <h2 className="font-heading text-lg lg:text-xl text-slate-900">
                        {selectedDoc.number || "Brouillon"}
                      </h2>
                      {renderStatusBadge(selectedDoc.status)}
                      {einvoiceStatus && einvoiceStatus.einvoice_status !== 'not_applicable' && type === "invoice" && (
                        <span className="inline-flex items-center gap-1 text-xs text-[#26788A] bg-teal-50 px-1.5 py-0.5 rounded" data-testid="einvoice-mini-badge">
                          <Zap size={10} />
                          E-Facture
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 mt-1 text-sm lg:text-base">{clientName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xl lg:text-2xl font-semibold text-slate-900">
                      {formatCurrency(selectedDoc.total_ttc)}
                    </p>
                    {selectedDoc.amount_due !== selectedDoc.total_ttc && selectedDoc.status !== "draft" && (
                      <p className="text-xs lg:text-sm text-slate-500 mt-1">
                        Reste dû: <span className="font-mono font-medium text-red-600">{formatCurrency(selectedDoc.amount_due)}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4 flex-wrap">
                  {selectedDoc.status === "draft" && (
                    <>
                      <Button
                        size="sm"
                        className="btn-primary"
                        onClick={() => handleValidate(selectedDoc)}
                        data-testid="validate-document-btn"
                      >
                        <CheckCircle2 size={14} className="mr-1.5" />
                        Valider
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-outline"
                        onClick={() => navigate(`/document/${selectedDoc.id}`)}
                      >
                        <Edit size={14} className="mr-1.5" />
                        Modifier
                      </Button>
                    </>
                  )}
                  {selectedDoc.status !== "draft" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-outline"
                      onClick={() => navigate(`/document/${selectedDoc.id}`)}
                    >
                      {type === "quote" ? (
                        <>
                          <Edit size={14} className="mr-1.5" />
                          Modifier
                        </>
                      ) : (
                        <>
                          <Eye size={14} className="mr-1.5" />
                          Voir
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="btn-outline"
                    onClick={() => handleDownloadPdf(selectedDoc)}
                  >
                    <Download size={14} className="mr-1.5" />
                    PDF
                  </Button>
                  {/* Duplicate button */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="btn-outline"
                    onClick={() => handleDuplicate(selectedDoc)}
                    data-testid="duplicate-document-btn"
                  >
                    <Copy size={14} className="mr-1.5" />
                    Copier
                  </Button>
                  {type === "quote" && selectedDoc.status !== "draft" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-outline"
                      onClick={() => handleConvert(selectedDoc)}
                    >
                      <ArrowRight size={14} className="mr-1.5" />
                      Convertir
                    </Button>
                  )}
                  {/* Deposit invoice button - for accepted/pending quotes */}
                  {type === "quote" && (selectedDoc.status === "accepted" || selectedDoc.status === "pending") && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[#26788A] border-[#26788A]/30 hover:bg-[#26788A]/5"
                      onClick={() => {
                        setDepositMode("percentage");
                        setDepositValue(30);
                        setDepositDialogOpen(true);
                      }}
                      data-testid="create-deposit-invoice-btn"
                    >
                      <Receipt size={14} className="mr-1.5" />
                      Acompte
                    </Button>
                  )}
                  {/* Balance invoice button - for quotes with deposits */}
                  {type === "quote" && (selectedDoc.status === "accepted" || selectedDoc.status === "pending") && quoteDeposits && quoteDeposits.total_deposited > 0 && !quoteDeposits.balance_invoice_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      onClick={handleCreateBalance}
                      data-testid="create-balance-invoice-btn"
                    >
                      <CheckCircle2 size={14} className="mr-1.5" />
                      Solde
                    </Button>
                  )}
                  {/* Accept/Refuse buttons for quotes (not draft, not already accepted/refused/expired) */}
                  {type === "quote" && selectedDoc.status === "pending" && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700 border-green-200 hover:border-green-300 hover:bg-green-50"
                        onClick={() => handleChangeQuoteStatus("accepted")}
                      >
                        <ThumbsUp size={14} className="mr-1.5" />
                        Accepter
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50"
                        onClick={() => handleChangeQuoteStatus("refused")}
                      >
                        <ThumbsDown size={14} className="mr-1.5" />
                        Refuser
                      </Button>
                    </>
                  )}
                  {type === "invoice" && selectedDoc.status !== "draft" && selectedDoc.status !== "paid" && selectedDoc.status !== "rectified" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-outline"
                      onClick={() => {
                        setPaymentForm({
                          amount: selectedDoc.amount_due,
                          payment_date: new Date().toISOString().split("T")[0],
                          payment_method: "transfer",
                          reference: ""
                        });
                        setPaymentDialogOpen(true);
                      }}
                    >
                      <CreditCard size={14} className="mr-1.5" />
                      Paiement
                    </Button>
                  )}
                  {type === "invoice" && (selectedDoc.status === "paid" || selectedDoc.status === "partial") && payments.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300 hover:bg-orange-50"
                      onClick={handleCancelAllPayments}
                      data-testid="revert-payment-btn"
                    >
                      <Undo2 size={14} className="mr-1.5" />
                      Annuler le règlement
                    </Button>
                  )}
                  {/* Credit Note button - for validated invoices */}
                  {type === "invoice" && selectedDoc.status !== "draft" && selectedDoc.status !== "rectified" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-outline"
                      onClick={handleOpenCreditNoteDialog}
                      disabled={creditNoteLoading}
                    >
                      <FileMinus size={14} className="mr-1.5" />
                      Avoir
                    </Button>
                  )}
                  {/* Rectify button - for validated invoices (not drafts, not already rectified) */}
                  {type === "invoice" && selectedDoc.status !== "draft" && selectedDoc.status !== "rectified" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="btn-outline text-amber-600 hover:text-amber-700 border-amber-200 hover:border-amber-300"
                      onClick={() => setRectifyDialogOpen(true)}
                    >
                      <FileEdit size={14} className="mr-1.5" />
                      Rectifier
                    </Button>
                  )}
                  {selectedDoc.status === "draft" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(selectedDoc)}
                      data-testid="delete-document-btn"
                    >
                      <Trash2 size={14} className="mr-1.5" />
                      Supprimer
                    </Button>
                  )}
                  {/* Cancel button - for validated documents (not draft, not already cancelled/rectified) */}
                  {selectedDoc.status !== "draft" && selectedDoc.status !== "cancelled" && selectedDoc.status !== "rectified" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-orange-600 border-orange-200 hover:bg-orange-50"
                      onClick={() => handleCancel(selectedDoc)}
                      data-testid="cancel-document-btn"
                    >
                      <XCircle size={14} className="mr-1.5" />
                      Annuler
                    </Button>
                  )}
                  {/* Force delete - for any non-draft document */}
                  {selectedDoc.status !== "draft" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(selectedDoc)}
                      data-testid="force-delete-document-btn"
                    >
                      <Trash2 size={14} className="mr-1.5" />
                      Supprimer
                    </Button>
                  )}
                </div>
              </div>

              {/* Detail Content */}
              <div className="p-6 space-y-6">
                {/* Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Date d'émission</p>
                    <p className="text-sm text-slate-900 mt-1">{formatDate(selectedDoc.issue_date)}</p>
                  </div>
                  {selectedDoc.due_date && (
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Échéance</p>
                      <p className="text-sm text-slate-900 mt-1">{formatDate(selectedDoc.due_date)}</p>
                    </div>
                  )}
                  {selectedDoc.subject && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Objet</p>
                      <p className="text-sm text-slate-900 mt-1">{selectedDoc.subject}</p>
                    </div>
                  )}
                </div>

                {/* Lines */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Lignes</p>
                  <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {selectedDoc.lines?.map((line, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900 font-medium">{line.name}</p>
                          {line.description && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{line.description}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {line.quantity} × {formatCurrency(line.unit_price_ht)} · TVA {line.vat_rate}%
                          </p>
                        </div>
                        <p className="font-mono text-sm font-medium text-slate-900 ml-4">
                          {formatCurrency(line.total_ht)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sous-total HT</span>
                      <span className="font-mono text-slate-900">{formatCurrency(selectedDoc.subtotal_ht)}</span>
                    </div>
                    {selectedDoc.total_discount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Remise</span>
                        <span className="font-mono text-slate-900">-{formatCurrency(selectedDoc.total_discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Total HT</span>
                      <span className="font-mono text-slate-900">{formatCurrency(selectedDoc.total_ht)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">TVA</span>
                      <span className="font-mono text-slate-900">{formatCurrency(selectedDoc.total_vat)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-slate-200">
                      <span className="font-medium text-slate-900">Total TTC</span>
                      <span className="font-mono font-semibold text-slate-900">{formatCurrency(selectedDoc.total_ttc)}</span>
                    </div>
                    {selectedDoc.deposit_applied > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Acompte</span>
                        <span className="font-mono text-slate-900">-{formatCurrency(selectedDoc.deposit_applied)}</span>
                      </div>
                    )}
                    {selectedDoc.amount_paid > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Payé</span>
                        <span className="font-mono text-green-600">-{formatCurrency(selectedDoc.amount_paid)}</span>
                      </div>
                    )}
                    {(selectedDoc.deposit_applied > 0 || selectedDoc.amount_paid > 0) && (
                      <div className="flex justify-between pt-2 border-t border-slate-200">
                        <span className="font-medium text-slate-900">Reste à payer</span>
                        <span className="font-mono font-semibold text-slate-900">{formatCurrency(selectedDoc.amount_due)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Deposit Summary (Acomptes) - on quotes */}
                {type === "quote" && quoteDeposits && quoteDeposits.deposits.length > 0 && (
                  <div data-testid="deposit-summary-section">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Receipt size={12} className="text-[#26788A]" />
                      Acomptes facturés
                    </p>
                    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
                      {/* Progress bar */}
                      <div>
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>{quoteDeposits.deposit_percentage_used}% facturé</span>
                          <span>Reste : {formatCurrency(quoteDeposits.remaining_ttc)}</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div
                            className="bg-[#26788A] rounded-full h-2 transition-all duration-300"
                            style={{ width: `${Math.min(quoteDeposits.deposit_percentage_used, 100)}%` }}
                          />
                        </div>
                      </div>
                      {/* Deposit list */}
                      <div className="divide-y divide-slate-100">
                        {quoteDeposits.deposits.map((dep, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-900 font-medium">{dep.number || "Brouillon"}</span>
                              {dep.deposit_percentage && (
                                <span className="text-xs text-slate-500">({dep.deposit_percentage}%)</span>
                              )}
                              <Badge className="text-[10px]" style={{
                                backgroundColor: dep.status === 'paid' ? '#dcfce7' : dep.status === 'pending' ? '#fef3c7' : '#f1f5f9',
                                color: dep.status === 'paid' ? '#166534' : dep.status === 'pending' ? '#92400e' : '#475569',
                              }}>
                                {dep.status === 'paid' ? 'Payée' : dep.status === 'pending' ? 'En attente' : dep.status === 'draft' ? 'Brouillon' : dep.status}
                              </Badge>
                            </div>
                            <span className="font-mono text-slate-900">{formatCurrency(dep.total_ttc)}</span>
                          </div>
                        ))}
                      </div>
                      {/* Total */}
                      <div className="flex justify-between pt-2 border-t border-slate-200 text-sm font-medium">
                        <span className="text-slate-700">Total acomptes</span>
                        <span className="text-[#26788A] font-mono">{formatCurrency(quoteDeposits.total_deposited)}</span>
                      </div>
                      {quoteDeposits.balance_invoice_id && (
                        <div className="text-xs text-emerald-600 bg-emerald-50 p-2 rounded">
                          Facture de solde créée
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Deposit source info - on deposit invoices */}
                {selectedDoc.is_deposit && selectedDoc.deposit_source_quote_number && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm">
                    <span className="text-teal-700">Acompte sur devis </span>
                    <span className="font-medium text-teal-900">{selectedDoc.deposit_source_quote_number}</span>
                    {selectedDoc.deposit_percentage && (
                      <span className="text-teal-600"> ({selectedDoc.deposit_percentage}%)</span>
                    )}
                  </div>
                )}

                {selectedDoc.is_balance && selectedDoc.deposit_source_quote_number && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                    <span className="text-emerald-700">Facture de solde - Devis </span>
                    <span className="font-medium text-emerald-900">{selectedDoc.deposit_source_quote_number}</span>
                  </div>
                )}

                {/* Rectification Info */}
                {selectedDoc.status === "rectified" && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-purple-700 mb-2">
                      Cette facture a été rectifiée
                    </p>
                    {selectedDoc.rectified_by_invoice_id && (
                      <Button
                        size="sm"
                        variant="link"
                        className="text-purple-600 p-0 h-auto"
                        onClick={() => navigate(`/document/${selectedDoc.rectified_by_invoice_id}`)}
                      >
                        Voir la facture rectificative →
                      </Button>
                    )}
                  </div>
                )}

                {/* Is Rectification Info */}
                {selectedDoc.is_rectification && selectedDoc.rectifies_invoice_number && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      Facture rectificative de <span className="font-medium">{selectedDoc.rectifies_invoice_number}</span>
                    </p>
                  </div>
                )}

                {/* Payments */}
                {type === "invoice" && payments.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Paiements reçus</p>
                      {payments.length > 0 && selectedDoc.status !== "rectified" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 text-xs h-6"
                          onClick={handleCancelAllPayments}
                        >
                          <XCircle size={12} className="mr-1" />
                          Tout annuler
                        </Button>
                      )}
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
                      {payments.map((payment, idx) => (
                        <div key={idx} className="px-4 py-3 flex items-center justify-between group">
                          <div>
                            <p className="text-sm text-slate-900">{formatDate(payment.payment_date)}</p>
                            <p className="text-xs text-slate-500">
                              {payment.payment_method === "transfer" ? "Virement" :
                               payment.payment_method === "check" ? "Chèque" :
                               payment.payment_method === "cash" ? "Espèces" :
                               payment.payment_method === "card" ? "Carte" : "Autre"}
                              {payment.reference && ` · ${payment.reference}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-sm font-medium text-green-600">
                              +{formatCurrency(payment.amount)}
                            </p>
                            {selectedDoc.status !== "rectified" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                data-testid={`cancel-payment-btn-${idx}`}
                                onClick={() => handleCancelPayment(payment.id)}
                                title="Annuler ce paiement"
                              >
                                <Undo2 size={14} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* E-Invoicing Section */}
                {type === "invoice" && selectedDoc.status !== "draft" && einvoiceStatus && (
                  <div data-testid="einvoice-section">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Zap size={12} className="text-[#26788A]" />
                        E-Facture
                      </p>
                      <Badge 
                        className="text-xs"
                        style={{
                          backgroundColor: 
                            einvoiceStatus.einvoice_status === 'accepted_by_pdp' ? '#dcfce7' :
                            einvoiceStatus.einvoice_status === 'sent_to_pdp' ? '#dbeafe' :
                            einvoiceStatus.einvoice_status === 'ready' ? '#fef3c7' :
                            einvoiceStatus.einvoice_status === 'rejected_by_pdp' ? '#fee2e2' :
                            einvoiceStatus.einvoice_status === 'error' ? '#fee2e2' :
                            '#f1f5f9',
                          color:
                            einvoiceStatus.einvoice_status === 'accepted_by_pdp' ? '#166534' :
                            einvoiceStatus.einvoice_status === 'sent_to_pdp' ? '#1e40af' :
                            einvoiceStatus.einvoice_status === 'ready' ? '#92400e' :
                            einvoiceStatus.einvoice_status === 'rejected_by_pdp' ? '#991b1b' :
                            einvoiceStatus.einvoice_status === 'error' ? '#991b1b' :
                            '#475569',
                        }}
                        data-testid="einvoice-status-badge"
                      >
                        {einvoiceStatus.einvoice_status_label}
                      </Badge>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
                      <div className="flex gap-2 flex-wrap">
                        {einvoiceStatus.can_transmit && (
                          <>
                            {einvoiceStatus.einvoice_status === 'not_applicable' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="btn-outline text-[#26788A]"
                                onClick={handlePrepareEinvoice}
                                disabled={einvoiceLoading}
                                data-testid="prepare-einvoice-btn"
                              >
                                {einvoiceLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Zap size={14} className="mr-1.5" />}
                                Préparer
                              </Button>
                            )}
                            {einvoiceStatus.einvoice_status === 'ready' && (
                              <Button
                                size="sm"
                                className="bg-[#26788A] hover:bg-[#006060] text-white"
                                onClick={handleTransmitEinvoice}
                                disabled={einvoiceLoading}
                                data-testid="transmit-einvoice-btn"
                              >
                                {einvoiceLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Zap size={14} className="mr-1.5" />}
                                Transmettre PDP
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-outline"
                          onClick={handleDownloadXml}
                          data-testid="download-xml-btn"
                        >
                          <FileCode size={14} className="mr-1.5" />
                          XML
                        </Button>
                        {einvoiceStatus.transmission_count > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="btn-outline"
                            onClick={handleLoadEinvoiceHistory}
                            data-testid="einvoice-history-btn"
                          >
                            <History size={14} className="mr-1.5" />
                            Historique ({einvoiceStatus.transmission_count})
                          </Button>
                        )}
                      </div>
                      {einvoiceStatus.pdp_id && (
                        <p className="text-xs text-slate-500">
                          ID PDP: <span className="font-mono">{einvoiceStatus.pdp_id}</span>
                        </p>
                      )}
                      {einvoiceStatus.error_message && (
                        <p className="text-xs text-red-600">{einvoiceStatus.error_message}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Client Info */}
                {selectedDoc.customer_snapshot && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Client</p>
                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                      <p className="font-medium text-slate-900">{clientName}</p>
                      {selectedDoc.customer_snapshot.address && (
                        <p className="text-sm text-slate-600 mt-1">
                          {selectedDoc.customer_snapshot.address}<br />
                          {selectedDoc.customer_snapshot.postal_code} {selectedDoc.customer_snapshot.city}
                        </p>
                      )}
                      {selectedDoc.customer_snapshot.email && (
                        <p className="text-sm text-slate-500 mt-2">{selectedDoc.customer_snapshot.email}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Montant</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 input-field font-mono"
              />
            </div>
            <div>
              <Label className="text-slate-700">Date</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                className="mt-1.5 input-field"
              />
            </div>
            <div>
              <Label className="text-slate-700">Mode de paiement</Label>
              <Select 
                value={paymentForm.payment_method}
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger className="mt-1.5 input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Virement</SelectItem>
                  <SelectItem value="check">Chèque</SelectItem>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="card">Carte bancaire</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Référence (optionnel)</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="N° de chèque, référence..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button onClick={handleAddPayment} className="btn-primary">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Note Dialog */}
      <Dialog open={creditNoteDialogOpen} onOpenChange={setCreditNoteDialogOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Créer un avoir</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                Facture <span className="font-medium">{selectedDoc?.number}</span> · {formatCurrency(selectedDoc?.total_ttc)}
              </p>
            </div>
            
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="creditType"
                  value="total"
                  checked={creditNoteType === "total"}
                  onChange={() => setCreditNoteType("total")}
                  className="text-[#26788A]"
                />
                <span className="text-sm">Avoir total (toutes les lignes)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="creditType"
                  value="partial"
                  checked={creditNoteType === "partial"}
                  onChange={() => setCreditNoteType("partial")}
                  className="text-[#26788A]"
                />
                <span className="text-sm">Avoir partiel (modifier les quantités)</span>
              </label>
            </div>

            <div>
              <Label className="text-slate-700">Motif (optionnel)</Label>
              <Input
                value={creditNoteReason}
                onChange={(e) => setCreditNoteReason(e.target.value)}
                className="mt-1.5 input-field"
                placeholder="Ex: Remise commerciale, erreur de facturation..."
              />
            </div>

            {creditNoteType === "partial" && creditNoteLines.length > 0 && (
              <div>
                <Label className="text-slate-700 mb-2 block">Lignes (quantité 0 = exclue)</Label>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-auto">
                  {creditNoteLines.map((line, idx) => (
                    <div key={idx} className="p-3 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{line.name}</p>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(line.unit_price_ht)} HT · TVA {line.vat_rate}%
                        </p>
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={line.quantity}
                          onChange={(e) => updateCreditNoteLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="input-field text-center text-sm"
                        />
                      </div>
                      <div className="w-24 text-right font-mono text-sm">
                        {formatCurrency(line.quantity * line.unit_price_ht)}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Total avoir : <span className="font-mono font-medium">
                    {formatCurrency(creditNoteLines.reduce((sum, l) => sum + (l.quantity * l.unit_price_ht), 0))} HT
                  </span>
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditNoteDialogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button 
              onClick={handleCreateCreditNote} 
              className="btn-primary"
              disabled={creditNoteLoading}
            >
              {creditNoteLoading ? "Création..." : "Créer l'avoir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rectify Confirmation Dialog */}
      <Dialog open={rectifyDialogOpen} onOpenChange={setRectifyDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Rectifier la facture</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Cette action va :
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside space-y-1">
                <li>Créer un <span className="font-medium">avoir d'annulation</span> pour cette facture</li>
                <li>Créer une <span className="font-medium">nouvelle facture (brouillon)</span> que vous pourrez modifier</li>
                <li>Marquer cette facture comme "Rectifiée"</li>
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              Facture concernée : <span className="font-medium">{selectedDoc?.number}</span> ({formatCurrency(selectedDoc?.total_ttc)})
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRectifyDialogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button 
              onClick={handleRectifyInvoice} 
              className="bg-amber-500 hover:bg-amber-600 text-white"
              disabled={rectifyLoading}
            >
              {rectifyLoading ? "Rectification..." : "Confirmer la rectification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* E-Invoice History Dialog */}
      <Dialog open={showEinvoiceHistory} onOpenChange={setShowEinvoiceHistory}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap size={18} className="text-[#26788A]" />
              Historique des transmissions e-facture
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-96 overflow-auto">
            {einvoiceHistory.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Aucune transmission enregistrée</p>
            ) : (
              <div className="space-y-3">
                {einvoiceHistory.map((t, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <Badge
                        className="text-xs"
                        style={{
                          backgroundColor:
                            t.status === 'accepted_by_pdp' ? '#dcfce7' :
                            t.status === 'sent_to_pdp' ? '#dbeafe' :
                            t.status === 'rejected_by_pdp' ? '#fee2e2' :
                            '#f1f5f9',
                          color:
                            t.status === 'accepted_by_pdp' ? '#166534' :
                            t.status === 'sent_to_pdp' ? '#1e40af' :
                            t.status === 'rejected_by_pdp' ? '#991b1b' :
                            '#475569',
                        }}
                      >
                        {t.status === 'sent_to_pdp' ? 'Transmise' :
                         t.status === 'accepted_by_pdp' ? 'Acceptée' :
                         t.status === 'rejected_by_pdp' ? 'Rejetée' : t.status}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {t.transmission_date ? new Date(t.transmission_date).toLocaleString("fr-FR") : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-600">
                      {t.pdp_id && (
                        <div>ID PDP: <span className="font-mono">{t.pdp_id}</span></div>
                      )}
                      {t.pdp_provider && (
                        <div>Fournisseur: {t.pdp_provider}</div>
                      )}
                      {t.pdp_response_message && (
                        <div className="col-span-2">{t.pdp_response_message}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEinvoiceHistory(false)} className="btn-outline">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deposit Invoice Dialog */}
      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={18} className="text-[#26788A]" />
              Facture d'acompte
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedDoc && (
              <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                <p>Devis : <strong>{selectedDoc.number}</strong></p>
                <p>Total TTC : <strong>{formatCurrency(selectedDoc.total_ttc)}</strong></p>
                {quoteDeposits && quoteDeposits.total_deposited > 0 && (
                  <>
                    <p className="text-amber-600 mt-1">Déjà facturé : {formatCurrency(quoteDeposits.total_deposited)} ({quoteDeposits.deposit_percentage_used}%)</p>
                    <p className="text-[#26788A]">Reste : {formatCurrency(quoteDeposits.remaining_ttc)}</p>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                variant={depositMode === "percentage" ? "default" : "outline"}
                className={depositMode === "percentage" ? "bg-[#26788A] text-white" : "btn-outline"}
                onClick={() => { setDepositMode("percentage"); setDepositValue(30); }}
              >
                <Percent size={14} className="mr-1" />
                Pourcentage
              </Button>
              <Button
                size="sm"
                variant={depositMode === "amount" ? "default" : "outline"}
                className={depositMode === "amount" ? "bg-[#26788A] text-white" : "btn-outline"}
                onClick={() => { setDepositMode("amount"); setDepositValue(""); }}
              >
                Montant HT
              </Button>
            </div>

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                {depositMode === "percentage" ? "Pourcentage (%)" : "Montant HT (€)"}
              </Label>
              <div className="relative mt-2">
                <Input
                  type="number"
                  min={0}
                  max={depositMode === "percentage" ? 100 : undefined}
                  step={depositMode === "percentage" ? 5 : 0.01}
                  value={depositValue}
                  onChange={(e) => setDepositValue(e.target.value)}
                  className="input-field pr-10"
                  placeholder={depositMode === "percentage" ? "30" : "1000.00"}
                  data-testid="deposit-value-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                  {depositMode === "percentage" ? "%" : "€"}
                </span>
              </div>
            </div>

            {/* Preview */}
            {depositValue && selectedDoc && (
              <div className="bg-[#26788A]/5 border border-[#26788A]/20 rounded-lg p-3 text-sm">
                <p className="text-[#26788A] font-medium">
                  Montant TTC estimé :{" "}
                  {depositMode === "percentage"
                    ? formatCurrency((selectedDoc.total_ttc * parseFloat(depositValue || 0)) / 100)
                    : formatCurrency(parseFloat(depositValue || 0) * (1 + (selectedDoc.total_vat / selectedDoc.total_ht || 0.2)))
                  }
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositDialogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button
              onClick={handleCreateDeposit}
              disabled={depositLoading || !depositValue || parseFloat(depositValue) <= 0}
              className="btn-primary"
              data-testid="confirm-deposit-btn"
            >
              {depositLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Receipt size={16} className="mr-2" />}
              Créer la facture d'acompte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
